import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Button from "../../Common/FormElements/Button/Button";
import {
  deleteIaPrompt,
  fetchIaPrompts,
  saveIaPrompt,
} from "../../../REDUX/actions/iaAdmin";

const emptyPrompt = { title: "", content: "", active: true, ordre: 0 };

/**
 * Gestion des instructions complementaires donnees a l'assistant.
 *
 * Le prompt de base et l'apercu du prompt final viennent du serveur
 * (utils/iaPrompt.js) et sont affiches en lecture seule : l'administrateur doit
 * voir a quoi ses instructions s'ajoutent, mais il ne peut pas les modifier, car
 * elles portent les garde-fous (perimetre, interdiction d'inventer).
 */
const IaAdminPrompts = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { prompts, promptsLoading, basePrompt, promptPreview } = useSelector(
    (state) => state.iaAdminReducer
  );
  const [draft, setDraft] = useState(emptyPrompt);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    dispatch(fetchIaPrompts());
  }, [dispatch]);

  const submit = () => {
    if (!draft.title.trim() || !draft.content.trim()) return;
    dispatch(saveIaPrompt(draft, t));
    setDraft(emptyPrompt);
  };

  return (
    <div className="d-flex flex-column gap-4">
      <div className="iaAdminCard br5 bg-lightGrey p-3">
        <div className="alignH justify-content-between mb-2">
          <p className="f14 sBold iaBlue mb-0">
            {t("Base system prompt (read-only)")}
          </p>
          <button
            type="button"
            className="border-0 bg-transparent f13 iaBlue"
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? t("Show base prompt") : t("Show final prompt")}
          </button>
        </div>

        <pre className="f12 normal text-lightBlack mb-0 iaBasePrompt">
          {showPreview ? promptPreview : basePrompt}
        </pre>

        <p className="f12 normal text-lightBlack mb-0 mt-2">
          {t(
            "Your instructions below are appended after these rules and cannot override them."
          )}
        </p>
      </div>

      <div className="iaAdminCard br5 b-1-darkGrey p-3">
        <p className="f14 sBold iaBlue mb-3">
          {draft.id ? t("Edit the instruction") : t("New instruction")}
        </p>

        <div className="d-flex flex-column gap-2">
          <input
            className="br5 p-2 b-1-darkGrey f14 w100"
            /* Le titre ne part pas dans le prompt : buildSystemPrompt ne
               concatene que le champ `content`. C'est un repere pour
               l'administrateur, l'utilisateur ne le voit jamais non plus. */
            placeholder={t("Title — for your organisation only, not sent to the assistant")}
            value={draft.title}
            onChange={(event) => setDraft({ ...draft, title: event.target.value })}
          />
          <textarea
            className="br5 p-2 b-1-darkGrey f14 w100"
            rows={4}
            placeholder={t("Instruction given to the assistant")}
            value={draft.content}
            onChange={(event) => setDraft({ ...draft, content: event.target.value })}
          />

          <div className="alignH gap-3">
            <label className="alignH gap-2 f13 normal text-lightBlack mb-0">
              {t("Display order")}
              <input
                type="number"
                className="br5 p-1 b-1-darkGrey f13 iaOrderInput"
                value={draft.ordre}
                onChange={(event) => setDraft({ ...draft, ordre: event.target.value })}
              />
            </label>

            <label className="alignH gap-2 f13 normal text-lightBlack mb-0">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(event) => setDraft({ ...draft, active: event.target.checked })}
              />
              {t("Active")}
            </label>

            <div className="ms-auto alignH gap-2">
              {draft.id && (
                <Button variant="outline" onClick={() => setDraft(emptyPrompt)}>
                  {t("Cancel")}
                </Button>
              )}
              <Button
                onClick={submit}
                disabled={!draft.title.trim() || !draft.content.trim()}
              >
                {t("Save")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {promptsLoading && (
        <span className="f14 normal text-lightBlack">{t("Loading")}...</span>
      )}

      <div className="d-flex flex-column gap-2">
        {prompts.map((prompt) => (
          <div
            key={prompt.id}
            className="iaAdminCard br5 b-1-darkGrey p-3 d-flex flex-column gap-2"
          >
            <div className="alignH justify-content-between">
              <div className="alignH gap-2">
                <span className={`iaDot ${prompt.active ? "iaDotOn" : "iaDotOff"}`} />
                <span className="f14 sBold text-lightBlack">{prompt.title}</span>
                <span className="f12 normal text-lightBlack">
                  ({t("Display order")} {prompt.ordre ?? 0})
                </span>
              </div>

              <div className="alignH gap-2">
                <button
                  type="button"
                  className="border-0 bg-transparent f13 iaBlue"
                  onClick={() =>
                    dispatch(saveIaPrompt({ ...prompt, active: !prompt.active }, t))
                  }
                >
                  {prompt.active ? t("Deactivate") : t("Activate")}
                </button>
                <button
                  type="button"
                  className="border-0 bg-transparent f13 iaBlue"
                  onClick={() => setDraft(prompt)}
                >
                  {t("Edit")}
                </button>
                <button
                  type="button"
                  className="border-0 bg-transparent f13 text-danger"
                  onClick={() => dispatch(deleteIaPrompt(prompt.id, t))}
                >
                  {t("Delete")}
                </button>
              </div>
            </div>

            <p className="f13 normal text-lightBlack mb-0 iaPromptContent">
              {prompt.content}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IaAdminPrompts;
